// La versión liviana (proxy) de un video del Estudio — ADR-2026-11-03-el-preview-usa-la-version-liviana.
//
// El backend la arma al lado del original con un nombre fijo (atlas-backend
// `src/services/estudio/proxy.ts`, `rutaDelProxy`: ESPEJO de esta función, si cambia
// una cambia la otra). El editor la usa SÓLO para mirar: el `<video>`/`<audio>` del
// preview, las miniaturas y las ondas. El export, la descarga, la captura de cuadro,
// el pincel y todo lo que manda el archivo a la IA siguen con el original (`clip.src`).
// Si no existe (todavía no se generó, falló o no hacía falta), el clip usa el original.
import { atlasBackendFetch } from '../../lib/atlasBackend';

const SUFIJO = '.preview.mp4';

/** `A/B/clip.mp4` → `A/B/clip.preview.mp4`. */
export function rutaDeLaVersionLiviana(storagePath: string): string {
  const barra = storagePath.lastIndexOf('/');
  const punto = storagePath.lastIndexOf('.');
  const base = punto > barra ? storagePath.slice(0, punto) : storagePath;
  return `${base}${SUFIJO}`;
}

/**
 * Lo que el preview reproduce: la versión liviana si la hay Y es del archivo que el clip
 * tiene ahora; si no, el original. Una edición que reemplaza el archivo (traducir, borrar
 * un objeto, corregir la voz…) deja la liviana vieja colgada: sin este chequeo el preview
 * seguía mostrando el video de antes (review Greptile #242).
 */
export function srcDelPreview(clip: {
  src?: string;
  srcPreview?: string;
  srcPreviewDe?: string;
  storagePath?: string;
}): string | undefined {
  if (clip.srcPreview && clip.srcPreviewDe && clip.srcPreviewDe === clip.storagePath)
    return clip.srcPreview;
  return clip.src;
}

/** Tope de rutas por pedido (el backend corta en 50). */
export const MAX_RUTAS_POR_PEDIDO = 50;

/**
 * Le pide al backend las versiones livianas que faltan. No espera nada ni avisa: si el
 * backend está apagado, caído o no la puede hacer, el editor sigue con el original.
 */
export async function pedirVersionesLivianas(projectId: string, storagePaths: string[]): Promise<void> {
  const rutas = [...new Set(storagePaths)].slice(0, MAX_RUTAS_POR_PEDIDO);
  if (rutas.length === 0) return;
  try {
    await atlasBackendFetch(
      '/v1/estudio/proxies',
      { project_id: projectId, storage_paths: rutas },
      { signal: AbortSignal.timeout(10_000) },
    );
  } catch {
    /* sin versión liviana: el preview sigue con el original */
  }
}

/**
 * Un `<video>` con el ORIGINAL, parado en el segundo `t`, para lo que necesita la
 * calidad completa (captura de cuadro, pincel): el del preview puede estar con la
 * versión liviana. Falla si no carga en `timeoutMs`.
 */
export function videoOriginalEn(src: string, t: number, timeoutMs = 20_000): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const el = document.createElement('video');
    el.crossOrigin = 'anonymous';
    el.muted = true;
    el.playsInline = true;
    el.preload = 'auto';
    const timer = window.setTimeout(() => {
      el.removeAttribute('src');
      reject(new Error('El video original no cargó a tiempo.'));
    }, timeoutMs);
    el.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('No se pudo cargar el video original.'));
    };
    el.onloadedmetadata = () => {
      el.onseeked = () => {
        window.clearTimeout(timer);
        resolve(el);
      };
      el.currentTime = Math.min(Math.max(0, t), Math.max(0, (el.duration || t) - 0.001));
    };
    el.src = src;
  });
}

/** Si el preview de este clip está reproduciendo la versión liviana (y no el original). */
export function usaLaVersionLiviana(clip: Parameters<typeof srcDelPreview>[0]): boolean {
  return !!clip.src && srcDelPreview(clip) !== clip.src;
}
